/**
 * Weight Tracking Service
 * CRUD operations for WeightRecords collection
 * Includes ADG calculation, growth curves, and anomaly detection
 *
 * @collection Users/{userId}/WeightRecords
 */

import { db } from "../../../firebase";

// ============================================================================
// WRITE OPERATIONS
// ============================================================================

/**
 * Add a weight record
 * @param {string} userId
 * @param {string} mac - Animal MAC
 * @param {Object} data - { weight (kg), measuredDate, method, notes }
 * @returns {Promise<string>} Document ID
 */
export async function addWeightRecord(userId, mac, data) {
	const timestamp = Date.now();
	const docId = timestamp.toString();

	const cleanData = Object.fromEntries(
		Object.entries(data).filter(([, v]) => v !== undefined)
	);

	const record = {
		...cleanData,
		weight: Number(cleanData.weight),
		MAC: mac,
		Uid: userId,
		t: timestamp,
		Archived: false,
	};

	await db
		.collection("Users")
		.doc(userId)
		.collection("WeightRecords")
		.doc(docId)
		.set(record);

	return docId;
}

/**
 * Add batch weight records (same date, multiple animals)
 * @param {string} userId
 * @param {Array<{ mac: string, weight: number }>} entries
 * @param {Object} sharedData - { measuredDate, method, notes }
 * @returns {Promise<Array<string>>} Document IDs
 */
export async function addBatchWeightRecords(userId, entries, sharedData) {
	const batch = db.batch();
	const docIds = [];

	for (const entry of entries) {
		const timestamp = Date.now();
		const docId = `${timestamp}_${entry.mac}`;
		docIds.push(docId);

		const record = {
			...sharedData,
			weight: Number(entry.weight),
			MAC: entry.mac,
			Uid: userId,
			t: timestamp,
			Archived: false,
		};

		const ref = db
			.collection("Users")
			.doc(userId)
			.collection("WeightRecords")
			.doc(docId);

		batch.set(ref, record);
	}

	await batch.commit();
	return docIds;
}

/**
 * Update a weight record
 */
export async function updateWeightRecord(userId, docId, updates) {
	const cleanUpdates = Object.fromEntries(
		Object.entries(updates).filter(([, v]) => v !== undefined)
	);

	if (cleanUpdates.weight !== undefined) {
		cleanUpdates.weight = Number(cleanUpdates.weight);
	}

	await db
		.collection("Users")
		.doc(userId)
		.collection("WeightRecords")
		.doc(docId)
		.update({ ...cleanUpdates, updatedAt: Date.now() });
}

/**
 * Archive a weight record
 */
export async function archiveWeightRecord(userId, docId) {
	await updateWeightRecord(userId, docId, { Archived: true, ArchivedAt: Date.now() });
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Get all weight records for an animal (chronological)
 */
export async function getWeightRecordsByAnimal(userId, mac) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("WeightRecords")
		.where("MAC", "==", mac)
		.where("Archived", "==", false)
		.orderBy("t", "asc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get all weight records for a user
 */
export async function getAllWeightRecords(userId) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("WeightRecords")
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get the latest weight record for an animal
 */
export async function getLatestWeight(userId, mac) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("WeightRecords")
		.where("MAC", "==", mac)
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.limit(1)
		.get();

	if (snapshot.empty) return null;
	const doc = snapshot.docs[0];
	return { id: doc.id, ...doc.data() };
}

// ============================================================================
// CALCULATIONS
// ============================================================================

/**
 * Calculate Average Daily Gain (ADG) for an animal
 * @param {Array} weightRecords - Sorted weight records (chronological)
 * @param {number|null} birthWeight - Birth weight in kg (optional)
 * @param {Date|number|null} birthDate - Birth date (optional)
 * @returns {{ adg: number|null, currentWeight: number|null, gainTotal: number|null, daysTracked: number }}
 */
export function calculateADG(weightRecords, birthWeight = null, birthDate = null) {
	if (!weightRecords?.length) {
		return { adg: null, currentWeight: null, gainTotal: null, daysTracked: 0 };
	}

	const sorted = [...weightRecords].sort((a, b) => a.t - b.t);
	const latest = sorted[sorted.length - 1];
	const currentWeight = latest.weight;

	// Use birth weight as starting point if available, else first record
	let startWeight, startTime;
	if (birthWeight != null && birthDate != null) {
		startWeight = birthWeight;
		startTime = typeof birthDate === "number" ? birthDate : new Date(birthDate).getTime();
	} else {
		startWeight = sorted[0].weight;
		startTime = sorted[0].t;
	}

	const endTime = latest.t;
	const daysTracked = Math.max(1, (endTime - startTime) / (24 * 60 * 60 * 1000));
	const gainTotal = currentWeight - startWeight;
	const adg = gainTotal / daysTracked;

	return {
		adg: Math.round(adg * 1000) / 1000, // 3 decimal places (kg/day)
		currentWeight,
		gainTotal: Math.round(gainTotal * 100) / 100,
		daysTracked: Math.round(daysTracked),
	};
}

/**
 * Build a growth curve data series for charting
 * @param {Array} weightRecords - Weight records
 * @param {number|null} birthWeight
 * @param {Date|number|null} birthDate
 * @returns {Array<{ dayOfLife: number, weight: number, date: number }>}
 */
export function buildGrowthCurve(weightRecords, birthWeight = null, birthDate = null) {
	const points = [];
	const birthTs = birthDate
		? typeof birthDate === "number" ? birthDate : new Date(birthDate).getTime()
		: null;

	if (birthWeight != null && birthTs != null) {
		points.push({ dayOfLife: 0, weight: birthWeight, date: birthTs });
	}

	const sorted = [...weightRecords].sort((a, b) => a.t - b.t);
	for (const record of sorted) {
		const dayOfLife = birthTs
			? Math.round((record.t - birthTs) / (24 * 60 * 60 * 1000))
			: null;
		points.push({
			dayOfLife,
			weight: record.weight,
			date: record.t,
		});
	}

	return points;
}

/**
 * Detect weight anomalies (sudden drops or stagnation)
 * @param {Array} weightRecords - Sorted weight records
 * @param {{ minADG?: number, maxDrop?: number }} thresholds
 * @returns {Array<{ type: string, message: string, record: Object }>}
 */
export function detectWeightAnomalies(weightRecords, thresholds = {}) {
	const { minADG = 0.3, maxDrop = 2 } = thresholds; // kg/day, kg
	const anomalies = [];

	if (weightRecords.length < 2) return anomalies;

	const sorted = [...weightRecords].sort((a, b) => a.t - b.t);

	for (let i = 1; i < sorted.length; i++) {
		const prev = sorted[i - 1];
		const curr = sorted[i];
		const daysBetween = (curr.t - prev.t) / (24 * 60 * 60 * 1000);

		if (daysBetween < 1) continue;

		const weightChange = curr.weight - prev.weight;

		// Weight drop
		if (weightChange < -maxDrop) {
			anomalies.push({
				type: "weight-drop",
				message: `Weight dropped ${Math.abs(weightChange).toFixed(1)}kg in ${Math.round(daysBetween)} days`,
				record: curr,
			});
		}

		// Stagnation (ADG below minimum over 7+ days)
		if (daysBetween >= 7 && weightChange / daysBetween < minADG) {
			anomalies.push({
				type: "low-adg",
				message: `ADG only ${(weightChange / daysBetween).toFixed(3)} kg/day over ${Math.round(daysBetween)} days`,
				record: curr,
			});
		}
	}

	return anomalies;
}
